import { Stack } from '@mui/material';
import React, { useState, useMemo, useEffect } from 'react';
import StaffReviewCardHeader from './StaffReviewCardHeader';
import StaffReviewChangesOnlyView from './StaffReviewChangesOnlyView';
import { useGetReviewQuery, useUpdateReviewMutation, useLogUserActionMutation, useUpdateDraftMutation, useCreateDraftCheckoutLogMutation } from '../../../api/staffReviewSlice';
import useStaffReviewUser from '../../Hooks/useStaffReviewUser';
import { useNavigate, useParams } from 'react-router';
import { differenceInMinutes } from 'date-fns';
import SubmitReviewConfirmation from './SubmitReviewConfirmation';
import CheckedOutCardDialog from '../CheckedOutCardDialog';
import CheckoutTimeoutDialog from '../CheckoutTimeoutDialog';
import AllItemsView from './AllItemsView';
import PhysicianNotes from './PhysicianNotes';
import CardDeletionRequest from './CardDeletionRequest';


function StaffReviewCardView() {
    const { draftId } = useParams();
    const { data: draft = {}, isSuccess: draftLoaded } = useGetReviewQuery(draftId);
    const { staffReviewUser: user } = useStaffReviewUser();
    const [updateReview] = useUpdateReviewMutation();
    const [updateDraft] = useUpdateDraftMutation();
    const [createCheckoutLog] = useCreateDraftCheckoutLogMutation();
    const [logUserAction] = useLogUserActionMutation();
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [timeOfLastUpdate, setTimeOfLastUpdate] = useState(new Date());
    const [isShowingSubmitConfirmation, setIsShowingSubmitConfirmation] = useState(false);
    const [isShowingCheckedOutDialog, setIsShowingCheckedOutDialog] = useState(false);
    const [isShowingTimeoutDialog, setIsShowingTimeoutDialog] = useState(false);
    const [isDeletionDenied, setIsDeletionDenied] = useState(false);
    const navigate = useNavigate();
    
    const [activeView, setActiveView] = useState('changesToReview');

    useEffect(() => {
        if (draftLoaded) {
            if (draft.status === 'applied' || draft.status === 'implemented') {
                setIsReadOnly(true);
                return;  // If the review is already applied, we don't need to check for checkout status since it will always be readonly. 
            }

            const checkoutStatus = draft.checkout;
            if (!checkoutStatus) {
                checkoutReview();
                return;
            }

            const currentTime = new Date();
            const timeSinceLastAction = differenceInMinutes(currentTime, new Date(draft.last_staff_review_action_time));
            const timeSinceCheckout = differenceInMinutes(currentTime, new Date(checkoutStatus.time_of_action));
             
            if (checkoutStatus.action === 'checked_out' && checkoutStatus.reviewer.id !== user.id && (timeSinceLastAction < 15 || timeSinceCheckout < 15)) {
                setIsShowingCheckedOutDialog(true);
                logAction('user_checkout_blocked', { draftId: draftId });
                setIsReadOnly(true);
            } else if (checkoutStatus.action === 'checked_in') {
                checkoutReview();
            } else if (checkoutStatus.action === 'checked_out' && timeSinceLastAction >= 15 && timeSinceCheckout >= 15) {
                checkoutReview();
            }
        }
    }, [draft, draftLoaded]);

    const canSubmit = useMemo(() => {
        if (isReadOnly) {
            return false;
        }

        let hasActionableReview = false;

        const allRecommendationsReviewed = Object.keys(draft.recommendations ?? {}).every((key) => {
            return draft.recommendations[key]?.every((rec) => {
                if (!rec.clinical_review || rec.clinical_review.status === 'closed') {
                    return true;
                }

                hasActionableReview = true;
                return rec.clinical_review.status === 'accepted' || rec.clinical_review.status === 'staff_implemented' || (rec.clinical_review.status === 'rejected' && rec.clinical_review.notes);
            });
        });

        return allRecommendationsReviewed && (hasActionableReview || isDeletionDenied);
    }, [draft.items, draft.recommendations, isReadOnly, isDeletionDenied]);

    async function logAction(action, data) {
        await logUserAction({
            action: action,
            data: data
        });
    }

    async function checkoutReview() {
        await createCheckoutLog({
            draftId: draftId,
            action: 'checked_out'
        });
    }

    async function handleUpdate(review, recommendation, newStatus, notes) {
        if (differenceInMinutes(new Date(), timeOfLastUpdate) >= 15) {
            setIsShowingTimeoutDialog(true);
            logAction('user_checkout_timed_out', { draftId: draftId });
            return;
        }

        await updateReview({
            review: review,
            draftId: draftId,
            recommendation: recommendation,
            newStatus: newStatus,
            notes: notes
        }).unwrap().then(() => {
            setTimeOfLastUpdate(new Date());
        });
    }

    function areAllRecsImplemented() {
        return Object.keys(draft.recommendations ?? {}).every((key) => {
            return draft.recommendations[key].every((rec) => {
                if (!rec.clinical_review) {
                    return true;
                }

                return rec.clinical_review.status === 'staff_implemented' || rec.clinical_review.status === 'rejected';
            });
        });
    }

    async function handleSubmitReview() {
        if (areAllRecsImplemented()) {            
            await updateDraft({
                draftId: draftId,
                status: 'implemented'
            }).unwrap().then(() => {
                createCheckoutLog({
                    draftId: draftId,
                    action: 'checked_in',
                    reason: 'review_submitted',
                });
            });
            setIsShowingSubmitConfirmation(false);
            return;
        }

        await updateDraft({
            draftId: draftId,
            status: 'applied'
        }).unwrap().then(() => {
            createCheckoutLog({
                draftId: draftId,
                action: 'checked_in',
                reason: 'review_submitted',
            });
        });
        setIsShowingSubmitConfirmation(false);
    }

    async function updateDraftStatus(status) {
        // add condition for denied
        if (status === 'denied') {
            logAction('card_deactivation_request_action_clinical', { draftId, status: 'denied' });
            setIsDeletionDenied(true);
            return;
        }
        await updateDraft({
            draftId: draftId,
            status: status
        }).unwrap().then(() => {
            logAction('card_deactivation_request_action_clinical', { draftId: draftId, status: status });
        });
    }

    return (
        <Stack py={2} px={4} spacing={2}>
            { draftLoaded && 
                <>
                    <StaffReviewCardHeader 
                        activeView={activeView} 
                        setActiveView={setActiveView} 
                        canSubmit={canSubmit} 
                        review={draft} 
                        setIsShowingSubmitConfirmation={setIsShowingSubmitConfirmation}
                    />
                    <PhysicianNotes 
                        notes={draft.physician_notes} 
                    />
                    <CardDeletionRequest
                        isMarkedForDeletion={draft.is_card_marked_for_deletion}
                        isReadOnly={isReadOnly}
                        updateDraft={(status) => updateDraftStatus(status)}
                        variant='clinical'
                    />
                    { activeView === 'changesToReview' && 
                        <StaffReviewChangesOnlyView 
                            variant='clinical'
                            review={draft} 
                            handleUpdate={handleUpdate}
                            isReadOnly={isReadOnly}
                        />
                    }
                    { activeView === 'allCardItems' &&
                        <AllItemsView
                            variant='clinical'
                            review={draft} 
                            handleUpdate={handleUpdate}
                            isReadOnly={isReadOnly}
                        />
                    }
                    <SubmitReviewConfirmation 
                        open={isShowingSubmitConfirmation} 
                        handleClose={() => setIsShowingSubmitConfirmation(false)} 
                        handleSubmit={handleSubmitReview} 
                    />
                    <CheckedOutCardDialog 
                        draft={draft}
                        open={isShowingCheckedOutDialog} 
                        handleClose={() => setIsShowingCheckedOutDialog(false)} 
                        returnToActiveReviews={() => navigate('/staffReview/activeReviews')} 
                    />
                    <CheckoutTimeoutDialog
                        draft={draft}
                        open={isShowingTimeoutDialog}
                        handleRefresh={() => window.location.reload()}
                    />
                </>
            }
        </Stack>
    );
}

export default StaffReviewCardView;
